import React from 'react';
import {IColumn, IColumnReorderOptions, IDetailsList} from '@fluentui/react';

// v2: v1 entries hold widths Fluent reported from its own layout passes, not user drags.
const STORAGE_PREFIX = 'ac.columnLayout.v2.';

interface IStoredLayout {
    widths: {[columnKey: string]: number};
    /** Column keys in the order the user arranged them; keys not listed keep their default position. */
    order: string[];
}

const EMPTY: IStoredLayout = {widths: {}, order: []};

const load = (storageKey: string): IStoredLayout => {
    try {
        const raw = window.localStorage.getItem(STORAGE_PREFIX + storageKey);
        const parsed = raw ? JSON.parse(raw) : undefined;
        if (!parsed || typeof parsed !== 'object') return EMPTY;
        return {
            widths: parsed.widths && typeof parsed.widths === 'object' ? parsed.widths : {},
            order: Array.isArray(parsed.order) ? parsed.order.filter((k: unknown) => typeof k === 'string') : []
        };
    } catch {
        return EMPTY;
    }
};

const save = (storageKey: string, layout: IStoredLayout) => {
    try {
        window.localStorage.setItem(STORAGE_PREFIX + storageKey, JSON.stringify(layout));
    } catch {
        // Storage unavailable (private window, quota, blocked): the layout still applies for this session.
    }
};

/**
 * Remembers how a user arranged a DetailsList in this browser: the widths they dragged and the order
 * they dropped the columns in. Per table (`storageKey`).
 *
 * Wire-up: `columns={arrange(columns)}`, `componentRef={listRef}`, `onColumnResize={onColumnResize}`,
 * `onColumnIsSizingChanged={onColumnIsSizingChanged}`, `columnReorderOptions={columnReorderOptions}`,
 * and call `applySavedWidths(arrangedColumns)` from an
 * effect after render. Widths go through the list's own `updateColumn` (the override a drag sets) so
 * columns keep their real minWidth and can still be shrunk.
 */
export const usePersistedColumnLayout = (storageKey: string) => {
    const [layout, setLayout] = React.useState<IStoredLayout>(() => load(storageKey));
    const listRef = React.useRef<IDetailsList>(null);
    // "key:width" pairs already pushed into the list, so re-renders do not re-apply (and re-fire) them.
    const appliedWidths = React.useRef<Set<string>>(new Set());
    // The keys in the order currently on screen, so a drop can be translated from indices to keys.
    const currentOrder = React.useRef<string[]>([]);
    /** How many pinned columns precede the arranged ones in the rendered list. */
    const fixedCount = React.useRef(0);
    // Fluent reports EVERY column's width after each layout pass through onColumnResize, so only
    // widths reported while the user is actually dragging a sizer (onColumnIsSizingChanged) count.
    const sizing = React.useRef<{key: string; width?: number} | undefined>(undefined);

    React.useEffect(() => {
        setLayout(load(storageKey));
        appliedWidths.current = new Set();
    }, [storageKey]);

    const update = React.useCallback((change: (current: IStoredLayout) => IStoredLayout) => {
        setLayout((current) => {
            const next = change(current);
            if (next !== current) save(storageKey, next);
            return next;
        });
    }, [storageKey]);

    const onColumnResize = React.useCallback((column?: IColumn, newWidth?: number) => {
        if (!column?.key || !newWidth || newWidth <= 0) return;
        if (sizing.current?.key !== column.key) return; // a layout pass, not the user
        sizing.current.width = Math.round(newWidth);
    }, []);

    const onColumnIsSizingChanged = React.useCallback((column?: IColumn, isSizing?: boolean) => {
        if (!column?.key) return;
        if (isSizing) {
            sizing.current = {key: column.key};
            return;
        }
        const finished = sizing.current;
        sizing.current = undefined;
        if (!finished || finished.key !== column.key || !finished.width) return;
        const rounded = finished.width;
        appliedWidths.current.add(`${column.key}:${rounded}`);
        update((current) => current.widths[column.key] === rounded
            ? current
            : {...current, widths: {...current.widths, [column.key]: rounded}});
    }, [update]);

    /**
     * Resizable by default, and in the remembered order (unknown columns keep their default slot).
     * `fixedLeading` columns (a drag grip, a row number) are pinned in front, never reordered or remembered,
     * and are counted so a drop's indices (which Fluent reports over the whole rendered list) line up.
     */
    const arrange = React.useCallback((columns: IColumn[], fixedLeading: IColumn[] = []): IColumn[] => {
        const resizable = columns.map((column) =>
            column.isResizable === false || column.isResizable === true ? column : {...column, isResizable: true});
        const byKey = new Map(resizable.map((column) => [column.key, column]));
        const known = layout.order.filter((key) => byKey.has(key));
        let arranged: IColumn[];
        if (known.length === 0) {
            arranged = resizable;
        } else {
            const knownSet = new Set(known);
            arranged = known.flatMap((key) => { const column = byKey.get(key); return column ? [column] : []; });
            // Columns the saved order has never seen slide back into their default positions.
            resizable.forEach((column, index) => {
                if (!knownSet.has(column.key)) arranged.splice(Math.min(index, arranged.length), 0, column);
            });
        }
        currentOrder.current = arranged.map((column) => column.key);
        fixedCount.current = fixedLeading.length;
        return [...fixedLeading, ...arranged];
    }, [layout.order]);

    const columnReorderOptions = React.useMemo<IColumnReorderOptions>(() => ({
        // Fixed leading columns cannot be dragged or dropped onto.
        frozenColumnCountFromStart: fixedCount.current,
        handleColumnReorder: (draggedIndex: number, targetIndex: number) => {
            // Fluent's indices span the rendered list, fixed leading columns included.
            const from = draggedIndex - fixedCount.current;
            const to = targetIndex - fixedCount.current;
            const keys = [...currentOrder.current];
            if (from < 0 || from >= keys.length || to < 0 || to >= keys.length) return;
            const [moved] = keys.splice(from, 1);
            keys.splice(to, 0, moved);
            update((current) => ({...current, order: keys}));
        }
    }), [update, layout.order]);

    /** Push any remembered width the list is not yet showing. Call from an effect after render. */
    const applySavedWidths = React.useCallback((columns: IColumn[]) => {
        const list = listRef.current;
        if (!list) {
            // No list on screen (e.g. an empty-state render): the next list mounts at default widths.
            appliedWidths.current = new Set();
            return;
        }
        for (const column of columns) {
            const saved = layout.widths[column.key];
            if (!saved) continue;
            const signature = `${column.key}:${saved}`;
            if (appliedWidths.current.has(signature)) continue;
            appliedWidths.current.add(signature);
            list.updateColumn(column, {width: saved});
        }
    }, [layout.widths]);

    return {listRef, arrange, onColumnResize, onColumnIsSizingChanged, columnReorderOptions, applySavedWidths, layout};
};
